import { LogEntryPhotoFile } from '../models/logs/photos/LogEntryPhotoFiles.interface';

/**
 * Triggers a browser download for each photo file of a log entry.
 *
 * We download the base64 bytes returned by our API (LogEntryPhotoFile) rather
 * than the Azure SAS `url`: the anchor `download` attribute is ignored for
 * cross-origin blob URLs, so a SAS link would open in a tab instead of saving.
 *
 * Files are saved one at a time with a short gap between them — browsers throttle
 * or block a burst of programmatic downloads fired in the same tick.
 *
 * @param files The photo files (with base64 `fileContent`) to save.
 * @returns Resolves once every download has been triggered.
 */
export async function downloadPhotoFiles(files: LogEntryPhotoFile[]): Promise<void> {
  if (!files || files.length === 0) {
    return;
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const blob = base64ToBlob(file.fileContent, file.contentType);
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = file.fileName || `photo-${i + 1}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Release the object URL after the click has been processed.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

    // Small gap so the browser doesn't suppress subsequent downloads.
    if (i < files.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }
}

/**
 * Converts a base64 string into a Blob of the given MIME type.
 */
function base64ToBlob(base64: string, contentType: string): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];
  const sliceSize = 512;

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType || 'application/octet-stream' });
}
